import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SocialLoginFragment from '@app/site/authentication/fragment/SocialRegistrationFragment';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { useTranslation } from '@app/component/translation/Translation';
import RegistrationInput from '@app/model/registration/input/RegistrationInput';
import RegistrationService from '@app/model/registration/service/RegistrationService';
import { useMutation } from '@tanstack/react-query';
import useAppForm from '@app/site/_form';
import * as z from 'zod/v4';
import PhoneSubForm from '@app/site/_form/field/PhoneSubForm';
import { Field, Label } from '@headlessui/react';
import { AbstractButton } from '@app/site/_form/button/AbstractButton';


const SignUpPage = React.memo(() => {

	const { t } = useTranslation();
	const toaster = useToaster();
	const navigate = useNavigate();
	const [isPending, setIsPending] = useState<boolean>();

	const RegisterFormValidation = z.object({
		name: z.string().nonempty('Name is required'),
		email: z.email('Email is required'),
		password: z.string().nonempty('Password is required'),
		phone: z.object({
			prefix: z.string().nonempty('Prefix is required'),
			number: z.string().nonempty('Phone number is required'),
		}),
		companyName: z.string().nonempty('Company name is required'),
		countryId: z.number({ error: 'Country is required' }).nullable().refine(val => val !== null, {
			message: 'Country is required',
		}),
		acceptTerms: z.boolean(),
	});

	const defaultFormValues: RegistrationInput = {
		name: '',
		email: '',
		password: '',
		phone: {
			prefix: '+30',
			number: '',
		},
		companyName: '',
		countryId: null,
		acceptTerms: false,
	};

	const form = useAppForm({
		defaultValues: defaultFormValues,
		validators: {
			onChange: RegisterFormValidation,
		},
		onSubmit: ({ value }) => registerMutation.mutateAsync(value).catch(()=>{}),
	});


	const registerMutation = useMutation({
		mutationFn: RegistrationService.register,
		onSuccess: () => {
			navigate('/verification-pending');
		},
		onError: (error: string) => {
			console.log(error);
			if (error == 'server.exception.registration.PASSWORD_DOES_NOT_VALIDATE') {
				// form.setError("password", {message: t(error)})
			} else
				toaster.error('Registration', t(error));
		},
	});

	return <div className="min-h-full bg-white">
		<div className="flex lg:h-dvh min-w-max content-center justify-center flex-1">
			<div className="flex flex-1 flex-col justify-center px-6 py-12">
				<div className="mx-auto w-full max-w-sm lg:w-96">
					<div>
						<img
							className="h-10 w-auto"
							src="/assets/img/brand/logo-icon.png"
							alt="RoomPulse"
						/>
						<h2 className="mt-8 text-2xl font-bold leading-9 tracking-tight text-gray-900">
							Create a new account
						</h2>
						<p className="mt-2 text-sm leading-6 text-gray-500">
							Already registered?{' '}
							<Link to="/sign-in" className="font-semibold text-blue-600 hover:text-blue-500"
										title="Click to sign in to your account">Click to sign in</Link>
						</p>
					</div>

					<div className="mt-10">
						<form.AppForm>
							<form onSubmit={
								(e) => {
									e.preventDefault();
									e.stopPropagation();
								}
							} className="space-y-6">
								<div className="grid grid-cols-1 gap-y-4 gap-x-8">
									<form.AppField name="name"
																 children={(field) => <field.TextField label="Name"
																																			 placeholder="Insert your name" />} />
									<form.AppField name="email"
																 children={(field) => <field.TextField label="Email"
																																			 placeholder="Insert your email" />} />
									<form.AppField name="companyName"
																 children={(field) => <field.TextField label="Company"
																																			 placeholder="Insert your company name" />} />
									<form.AppField name="countryId"
																 children={(field) => <field.CountryField label="Country"
																																					placeholder="Insert your country" />} />
									{/*@ts-ignore*/}
									<PhoneSubForm form={form} label="Phone Number" placeholder="Insert your phone number" />
									<form.AppField name="password"
																 children={(field) => <field.PasswordField label="Password"
																																					 placeholder="Insert your password" />} />
								</div>


								<div className="mt-8">
									<form.AppField name="acceptTerms"
																 children={(field) => <Field as="div" className="flex gap-x-4 sm:col-span-2 mt-3">
																	 <div className="flex h-6 items-center">
																		 <field.SwitchField id={"accept-terms"} label="Agree to the policy" />
																		 <Label htmlFor="accept-terms" className="text-sm leading-6 text-slate-600 ml-2">
																			 By selecting this, you agree to our{' '}
																			 <a href="/sdsd"
																					className="font-semibold text-blue-600 hover:text-blue-500">privacy&nbsp;policy</a>.
																		 </Label>
																	 </div>
																 </Field>} />

									<form.Subscribe selector={(state) => state}>
										{(state) => <AbstractButton state='primary' extraClassName='flex w-full mt-4' isSubmitting={state.isSubmitting || isPending} onClick={form.handleSubmit} disabled={!state.values.acceptTerms}>Sign up</AbstractButton>}
									</form.Subscribe>

								</div>

							</form>
						</form.AppForm>

						<div className="mt-10">
							<SocialLoginFragment onPending={setIsPending} />
						</div>
					</div>
				</div>
			</div>
			<div className="relative hidden flex-1 lg:block">
				<img
					className="absolute inset-0 h-full w-full object-cover"
					src="https://images.unsplash.com/photo-1587783456120-fa80c81a6c4c?q=80&w=2574&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
					alt=""
				/>
			</div>
		</div>
	</div>;
});

export default SignUpPage;